import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { Building2, CreditCard, Users, Mail, Monitor, KeyRound } from 'lucide-react';
import { cn } from '@/lib/cn';
import CompanySettings from './CompanySettings';
import UsersManager from './UsersManager';
import ClientsManager from './ClientsManager';
import InvitesManager from './InvitesManager';
import SubscriptionManager from './SubscriptionManager';
import ApiKeysManager from './ApiKeysManager';

const tabs = [
    { key: 'settings', label: 'Empresa', icon: Building2 },
    { key: 'subscription', label: 'Assinatura', icon: CreditCard },
    { key: 'users', label: 'Usuários', icon: Users },
    { key: 'invites', label: 'Convites', icon: Mail },
    { key: 'clients', label: 'Clientes', icon: Monitor },
    { key: 'apikeys', label: 'API Keys', icon: KeyRound },
];

export default function MyCompany() {
    const navigate = useNavigate();
    const location = useLocation();

    const getActiveKey = () => {
        const path = location.pathname;
        if (path.includes('/users')) return 'users';
        if (path.includes('/invites')) return 'invites';
        if (path.includes('/clients')) return 'clients';
        if (path.includes('/subscription')) return 'subscription';
        if (path.includes('/apikeys')) return 'apikeys';
        return 'settings';
    };

    const activeKey = getActiveKey();
    const activeTab = tabs.find((t) => t.key === activeKey);

    return (
        <div className="dash-scroll flex h-full w-full flex-col overflow-y-auto bg-[var(--dash-bg)]">
        <div
            className="mx-auto w-full max-w-6xl px-4 py-6 sm:px-6 lg:px-8 pb-16"
            style={{ color: 'var(--dash-text)' }}
        >
            {/* Header */}
            <div className="mb-6">
                <h1
                    className="text-xl font-semibold tracking-tight sm:text-2xl"
                    style={{ color: 'var(--dash-text)' }}
                >
                    Minha Empresa
                </h1>
                <p
                    className="mt-1 text-xs sm:text-sm"
                    style={{ color: 'var(--dash-text-muted)' }}
                >
                    Gerencie as configurações da sua organização
                </p>
            </div>

            {/* Tab Navigation */}
            <div
                className="relative mb-6 w-full overflow-x-auto border-b"
                style={{ borderColor: 'var(--dash-border)' }}
            >
                <nav className="flex min-w-fit gap-1 px-1">
                    {tabs.map((tab) => {
                        const Icon = tab.icon;
                        const active = tab.key === activeKey;
                        return (
                            <button
                                key={tab.key}
                                type="button"
                                onClick={() =>
                                    navigate(tab.key === 'settings' ? 'settings' : tab.key)
                                }
                                className={cn(
                                    'group relative flex shrink-0 items-center gap-2 rounded-t-lg px-3 py-3 text-sm font-medium transition-all duration-200 sm:px-4'
                                )}
                                style={{
                                    color: active
                                        ? 'var(--dash-text)'
                                        : 'var(--dash-text-muted)',
                                }}
                                onMouseEnter={(e) => {
                                    if (!active)
                                        e.currentTarget.style.color = 'var(--dash-text-soft)';
                                }}
                                onMouseLeave={(e) => {
                                    if (!active)
                                        e.currentTarget.style.color = 'var(--dash-text-muted)';
                                }}
                            >
                                <Icon
                                    size={15}
                                    strokeWidth={2.2}
                                    className="transition-transform duration-200 group-hover:scale-110"
                                    style={{
                                        color: active ? 'var(--dash-primary)' : 'currentColor',
                                    }}
                                />
                                <span className="hidden whitespace-nowrap sm:inline">
                                    {tab.label}
                                </span>
                                {active && (
                                    <span
                                        className="absolute bottom-0 left-0 h-[2px] w-full rounded-full"
                                        style={{ background: 'var(--dash-primary)' }}
                                    />
                                )}
                            </button>
                        );
                    })}
                </nav>
            </div>

            {/* Mobile active tab label */}
            <div className="mb-4 sm:hidden">
                <h2
                    className="text-base font-semibold"
                    style={{ color: 'var(--dash-text)' }}
                >
                    {activeTab?.label}
                </h2>
            </div>

            {/* Content */}
            <div className="animate-tab-fade-in">
                <Routes>
                    <Route index element={<Navigate to="settings" replace />} />
                    <Route path="settings" element={<CompanySettings />} />
                    <Route path="subscription" element={<SubscriptionManager />} />
                    <Route path="users" element={<UsersManager />} />
                    <Route path="invites" element={<InvitesManager />} />
                    <Route path="clients" element={<ClientsManager />} />
                    <Route path="apikeys" element={<ApiKeysManager />} />
                    <Route path="*" element={<Navigate to="settings" replace />} />
                </Routes>
            </div>

            <style>{`
                @keyframes tab-fade-in {
                    from { opacity: 0; transform: translateY(4px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-tab-fade-in { animation: tab-fade-in 0.25s ease-out; }
            `}</style>
        </div>
        </div>
    );
}
